import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Button } from '@mui/material';
import { observer } from 'mobx-react';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useI18n } from '../../../../../../application/language/useI18n';
import { Colors } from '../../../../../design/color/Colors';
import { ContainerContext } from '../../../../../../common/container/ContainerContext';
import { UserProjectRoleAction } from '../../../../../../service/projectRole/entity/actions/UserProjectRoleAction';
import { FormMultiAutocomplete } from '../../../../../design/form/FormMultiAutocomplete';
import { FormTextArea } from '../../../../../design/form/FormTextArea';
import { FormTextField } from '../../../../../design/form/FormTextField';
import { IAdminPageProperties } from '../../../IAdminPageProperties';
import { IUnitListAdminPageDomain } from '../learningUnitList/store/IUnitListAdminPageDomain';
import { UnitListAdminPageDomain } from '../learningUnitList/store/UnitListAdminPageDomain';
import { RecommendedUnits } from './components/RecommendedUnits';
import { UserProjectRoleAdminFormDomain } from './store/UserProjectRoleAdminFormDomain';
import { areArraysEqual } from './utils/areArraysEqual';

export interface IUserProjectRoleAdminFormProperties extends IAdminPageProperties {}

export const UserProjectRoleAdminForm = observer(({ layoutDomain }: IUserProjectRoleAdminFormProperties) => {
  const { translate } = useI18n();
  const container = useContext(ContainerContext);
  const [domain] = useState(new UserProjectRoleAdminFormDomain(layoutDomain, container));
  const [unitListAdminPageDomain] = useState<IUnitListAdminPageDomain>(new UnitListAdminPageDomain(layoutDomain));
  const { roleId = null } = useParams<{ roleId?: string }>();
  const uiEntity = domain.ui.model.entity;
  const [checkedUnits, setCheckedUnits] = useState<string[]>([]);
  const [initialUnits, setInitialUnitsList] = useState<string[]>([]);

  const onSave = async () => {
    let urlParams = areArraysEqual(checkedUnits, initialUnits) ? '' : '?unitsUpdating=true';
    const newId = await domain.save(urlParams);

    if (roleId || newId) {
      unitListAdminPageDomain.updateUnitsProjectRoles(roleId || newId, checkedUnits);
    }
  };

  useEffect(() => {
    domain.loadData(roleId);
    unitListAdminPageDomain.loadData();
  }, [domain, roleId, unitListAdminPageDomain]);

  return (
    <Box sx={{ height: 'calc(100vh - 90px)', display: 'flex', flexDirection: 'column', backgroundColor: Colors.bgElevated, borderRadius: '8px', border: `1px solid ${Colors.border}`, paddingBottom: '16px', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: '16px', py: '4px' }}>
        <Button onClick={domain.backToRolesPage}>
          <ArrowBackIcon sx={{ marginRight: '8px' }} />
          <span>{translate('phrases.projectRoles')}</span>
        </Button>
        <Box sx={{ display: 'flex', gap: '8px' }}>
          <Button variant="outlined" onClick={domain.cancelEdit}>
            {translate('phrases.cancel')}
          </Button>

          <Button
            variant="contained"
            color="primary"
            disabled={!unitListAdminPageDomain.ui.filtredGroupedUnits.list.length}
            onClick={onSave}
          >
            {translate('phrases.save')}
          </Button>
        </Box>
      </Box>

      <FormTextField
        errorMessage={domain.getValidationErrorFor('name')?.message}
        value={uiEntity.name}
        onChange={domain.getUpdateFieldHandler('name')}
        label={translate('projectRoles.name')}
        required={true}
      />

      <FormTextArea
        errorMessage={domain.getValidationErrorFor('description')?.message}
        value={uiEntity.description}
        onChange={domain.getUpdateFieldHandler('description')}
        label={translate('phrases.description')}
        required={true}
        paddingSizing={1}
      />

      <FormMultiAutocomplete
        values={domain.actionsValues}
        onChangeSelect={(values) => {
          if (!domain.ui.model.entity.permissions?.data) {
            domain.ui.model.setEntity({
              ...domain.ui.model.entity,
              permissions: {
                data: [],
              },
            });
          }
          const permissions = values.map((item) => {
            return {
              isGranted: true,
              action: item.value,
            };
          });
          domain.ui.model.setEntity({ ...domain.ui.model.entity, permissions: { data: permissions } });
        }}
        key="permissions"
        selected={
          !domain.ui.model.entity.permissions?.data
            ? [UserProjectRoleAction.showRegulators]
            : uiEntity.permissions?.data
                ?.filter((item) => {
                  const actionValue = domain.actionsValues.find((value) => value.value === item.action);
                  return actionValue?.value && item.isGranted;
                })
                .map((item) => {
                  const actionValue = domain.actionsValues.find((value) => value.value === item.action);
                  return actionValue?.value || '';
                })
        }
        label={translate('phrases.rights')}
      />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <RecommendedUnits
          layoutDomain={layoutDomain}
          currentRoleId={roleId}
          checkedUnits={checkedUnits}
          setCheckedUnits={setCheckedUnits}
          setInitialUnitsList={setInitialUnitsList}
        />
      </Box>
    </Box>
  );
});
